import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, Leaf, Truck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [model, setModel] = useState('');
  const { user } = useAuth();
  const { add } = useCart();
  const { format } = useCurrency();
  const { t, tModel } = useLanguage();
  const { settings } = useSiteSettings();
  const storeOpen = settings.store_open !== false;
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      setVariant(data.variants?.[0] || null);
      setModel(data.models?.[0] || '');
    });
  }, [id]);

  const modelLabel = useMemo(() => {
    if (!product) return t('product.model', 'Option');
    if (product.type === 'phone_case') return t('product.iphoneModel', 'iPhone model');
    if (product.type === 'sticker') return t('product.size', 'Size');
    if (product.type === 'label') return t('product.shapeSize', 'Shape / size');
    return t('product.model', 'Option');
  }, [product, t]);

  const resolvedPrice = useMemo(() => {
    if (!product) return 0;
    const mp = product.model_pricing || {};
    if (model && mp[model] != null) return Number(mp[model]);
    return Number(product.price || 0);
  }, [product, model]);

  const quickAdd = async () => {
    if (!storeOpen) { toast.error(t('closure.cannotAdd', "We're paused for new orders — you can browse but can't add to cart right now.")); return; }
    if (!user) { toast(t('designDetail.signInAddCart', 'Please sign in first')); navigate('/login'); return; }
    try {
      await add({
        product_id: product.id,
        variant: [model, variant].filter(Boolean).join(' / ') || variant || '',
        model: model || undefined,
        quantity: 1,
      });
      toast.success(t('designDetail.addedToast', 'Added to cart'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('designDetail.couldNotAdd', 'Failed to add'));
    }
  };

  if (!product) return <div className="p-10 text-white/60">{t('common.loading', 'Loading product...')}</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <SEO
        title={product.name}
        description={product.description || `Custom ${product.name} \u2014 design your own and get it printed with vibrant colors and fast delivery.`}
        path={`/product/${product.id}`}
        type="product"
        image={product.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "image": product.image,
          "brand": { "@type": "Brand", "name": "Jwalek" },
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": product.price,
            "availability": "https://schema.org/InStock",
            "url": `https://jwalek.com/product/${product.id}`,
          }
        }}
      />
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="jw-card overflow-hidden bg-[color:var(--jw-navy-950)]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover aspect-square" />
        </div>
        <div>
          <Badge className="bg-white/[0.06] border-white/10 text-white/80">{t(`productType.${product.type}`, product.type.replace('_', ' '))}</Badge>
          <h1 className="font-display text-4xl font-bold mt-3">{product.name}</h1>
          <p className="text-white/70 mt-3 max-w-lg">{product.description}</p>
          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-white" data-testid="product-resolved-price">{format(resolvedPrice)}</span>
            {model && (product.model_pricing || {})[model] != null && (
              <span className="text-white/50 text-xs" data-testid="product-model-price-note">{tModel(model)}</span>
            )}
            <span className="text-white/50 text-sm">{t('product.freeShipOver', 'Free shipping over')} {format(35)}</span>
          </div>

          <div className="mt-6">
            <SocialStrip entityType="product" entityId={product.id} layout="full" size="md" />
          </div>

          {product.models?.length > 0 && (
            <div className="mt-6">
              <p className="text-white/70 text-sm mb-2">{modelLabel}</p>
              <div className="flex gap-2 flex-wrap">
                {product.models.map(m => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`product-model-${m.replace(/\s+/g,'-').toLowerCase()}-button`}
                    data-selected={model === m ? 'true' : 'false'}
                    onClick={() => setModel(m)}
                    className="jw-chip"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >{tModel(m)}</button>
                ))}
              </div>
            </div>
          )}

          {product.variants?.length > 0 && (
            <div className="mt-6">
              <p className="text-white/70 text-sm mb-2">{t('product.finish', 'Finish')}</p>
              <div className="flex gap-2 flex-wrap">
                {product.variants.map(v => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`product-variant-${v.replace(/\s+/g,'-').toLowerCase()}-button`}
                    data-selected={variant === v ? 'true' : 'false'}
                    onClick={() => setVariant(v)}
                    className="jw-chip"
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >{tModel(v)}</button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/design/${product.id}`}>
              <Button data-testid="product-start-designing-button" className="jw-btn-glossy rounded-[14px] h-12 px-6">
                {t('product.startDesigning', 'Start Designing')} <ArrowRight className="ml-2 w-4 h-4 jw-flip-in-rtl" />
              </Button>
            </Link>
            <Button data-testid="product-add-to-cart-button" onClick={quickAdd} disabled={!storeOpen} className="jw-btn-gloss-ghost h-12 px-6 rounded-[14px] disabled:opacity-50 disabled:cursor-not-allowed">
              {storeOpen ? t('product.addBlank', 'Add blank to cart') : t('closure.addToCartLabel', 'Orders paused')}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8">
            <div className="jw-panel p-3 flex items-center gap-2 text-sm text-white/80"><ShieldCheck className="w-4 h-4 text-emerald-300" /> {t('product.qualityGuarantee', 'Quality Guarantee')}</div>
            <div className="jw-panel p-3 flex items-center gap-2 text-sm text-white/80"><Truck className="w-4 h-4 text-sky-300" /> {t('product.fastDelivery', 'Fast Delivery')}</div>
            <div className="jw-panel p-3 flex items-center gap-2 text-sm text-white/80"><Leaf className="w-4 h-4 text-emerald-300" /> {t('product.ecoFriendly', 'Eco Friendly')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
